(() => {
  'use strict';
  if (window.WellHomeQualityLayer) return;
  window.WellHomeQualityLayer = true;

  /*
    Capa de calidad para paginas que NO son de producto.

    Historico: aqui vivia una capa que pedia /products/<handle>.js por cada
    tarjeta de la portada para decidir si mostrar "Anadir al carrito" o
    "Elegir opciones", y que interceptaba el submit en fase de captura.
    Ambas cosas se han retirado:

      1. Esa decision ya se toma en servidor, en home-wellhome-commerce.liquid,
         con product.has_only_default_variant. La capa JS repetia por red algo
         que Liquid ya sabia al renderizar.
      2. El interceptor de submit llamaba a HTMLFormElement.prototype.submit(),
         que recarga la pagina saltandose los listeners y por tanto impedia
         que se abriera el cajon de carrito de Horizon.

    Lo que queda aqui son mejoras que no se pueden resolver en servidor.
  */

  const mobileFilters = window.matchMedia('(max-width: 989px)');
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const visibleFocusable = (panel) => Array.from(panel.querySelectorAll(focusableSelector))
    .filter((element) => element.getClientRects().length > 0 && !element.closest('[inert]'));

  /* --- Panel de filtros: semantica de dialogo solo cuando es cajon movil --- */

  const syncFilterPanel = (panel) => {
    const isMobile = mobileFilters.matches;
    const isOpen = panel.classList.contains('is-open');
    if (!isMobile) {
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
      panel.removeAttribute('aria-modal');
      panel.setAttribute('role', 'group');
      return;
    }
    if (isOpen) {
      panel.removeAttribute('inert');
      panel.setAttribute('aria-hidden', 'false');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('role', 'dialog');
    } else {
      panel.setAttribute('inert', '');
      panel.setAttribute('aria-hidden', 'true');
      panel.removeAttribute('aria-modal');
      panel.setAttribute('role', 'group');
    }
  };

  const initFilterPanels = (root = document) => {
    root.querySelectorAll('.cw-filters[data-filters-panel]').forEach((panel) => {
      if (panel.dataset.whQualityReady === 'true') return;
      panel.dataset.whQualityReady = 'true';
      syncFilterPanel(panel);
      const observer = new MutationObserver(() => syncFilterPanel(panel));
      observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
    });
  };

  const syncAllFilterPanels = () => document.querySelectorAll('.cw-filters[data-filters-panel]').forEach(syncFilterPanel);

  /*
    Respaldo de ordenacion: el <select> de orden usa el atributo form= para
    enviarse con el formulario de filtros, pero ese formulario solo existe si
    la coleccion tiene filtros configurados. Sin filtros, el select no haria
    nada; aqui se navega a mano.
  */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('.cw-sort select[data-sort]');
    if (!select) return;
    const formId = select.getAttribute('form');
    if (formId && document.getElementById(formId)) return;
    const url = new URL(window.location.href);
    if (select.value) url.searchParams.set('sort_by', select.value);
    else url.searchParams.delete('sort_by');
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });

  /* --- Accesibilidad del cajon de filtros en movil --- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-filters-open]');
    if (!trigger) return;
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('role', 'dialog');
    window.requestAnimationFrame(() => panel.querySelector('[data-filters-close]')?.focus());
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !mobileFilters.matches) return;
    const panel = document.querySelector('.cw-filters[data-filters-panel].is-open');
    if (!panel) return;
    const focusable = visibleFocusable(panel);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /*
    Las dos funciones siguientes tambien viven en wellhome-product-quality.js,
    que es el fichero que se carga en fichas de producto. Se mantienen aqui por
    si las secciones de relacionados u opiniones se anaden a otra plantilla.
  */

  const initRelatedProgress = (root = document) => {
    root.querySelectorAll('.whpl').forEach((section) => {
      if (section.dataset.whProgressReady === 'true') return;
      const track = section.querySelector('.whpl-track');
      const progress = section.querySelector('.whpl-progress span');
      if (!track || !progress) return;
      section.dataset.whProgressReady = 'true';

      let frame = null;
      const update = () => {
        frame = null;
        const travel = Math.max(0, track.scrollWidth - track.clientWidth);
        const thumb = track.scrollWidth ? Math.max(16, Math.min(100, (track.clientWidth / track.scrollWidth) * 100)) : 100;
        const available = 100 - thumb;
        const position = travel ? (track.scrollLeft / travel) * available : 0;
        progress.style.width = `${thumb}%`;
        progress.style.transform = `translateX(${position / Math.max(thumb, 1) * 100}%)`;
      };
      const requestUpdate = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      };

      track.addEventListener('scroll', requestUpdate, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(requestUpdate).observe(track);
      else window.addEventListener('resize', requestUpdate, { passive: true });
      update();
    });
  };

  const initReviewAppFallbacks = (root = document) => {
    root.querySelectorAll('.whpr').forEach((section) => {
      const appBlock = section.querySelector('.whpr-app');
      if (!appBlock) return;
      section.querySelectorAll('.whpr-card').forEach((card) => {
        card.hidden = true;
        card.setAttribute('aria-hidden', 'true');
      });
    });
  };

  const init = (root = document) => {
    initFilterPanels(root);
    initRelatedProgress(root);
    initReviewAppFallbacks(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  if (typeof mobileFilters.addEventListener === 'function') mobileFilters.addEventListener('change', syncAllFilterPanels);
  else mobileFilters.addListener(syncAllFilterPanels);
})();
